import { useCallback, useMemo, useRef } from 'react';
import { useFetchSurvey } from '../fetchSurvey/useFetchSurvey';
import { useSurveyPagination } from '../surveyPagination/useSurveyPagination';
import { useMarkAsStarted } from '../markAsStarted/useMarkAsStarted';
import { usePartialSave } from '../partialSave/usePartialSave';
import { useRecordQuestionActivity } from '../recordQuestionActivity/useRecordQuestionActivity';
import { isPartialSaveEnabled, resolvePartialSaveDebounceMs } from '../partialSave/types';
import { sdkLogger } from '../logger/logger';
import { toSdkLoggableError } from '../types/shared';
import { ACTIONS } from './constants';
import type { SurveyAction, UseSurveySDKProps, UseSurveySDKReturn } from './types';
import { EMPTY_CUSTOM_FIELD_VALUE_MAP } from '../surveyLogic/types/customFieldValues';
import {
  assignRankWithoutDuplicates,
  buildRankOrderFromOrderedOptionIds,
  normalizeRankOrderAnswers,
} from '../surveyLogic/fields/rankOrderUtils';
import { buildMcqOptionLevelKey } from '../surveyLogic/fields/mcqOtherUtils';
import type { RankOrderAnswers } from '../submitSurvey/types';

const EMPTY_PARTIAL_SAVE_RESULTS = {
  data: null,
  error: null,
  isLoading: false,
} as const;

/**
 * The main entry-point custom React hook for the survey SDK.
 */
export function useSurveySDK({ options }: UseSurveySDKProps = {}): UseSurveySDKReturn {
  sdkLogger.setEnabled(options?.debug ?? false);

  const hasUserInteractedRef = useRef(false);
  const hasSurveySessionStartedRef = useRef(false);

  const { survey, session, fetching, fetchError, mapError } = useFetchSurvey({
    instanceId: options?.instanceId,
    language: options?.language,
    placeholders: options?.placeholders,
  });

  const { markOnce, getInstanceCreatedTime } = useMarkAsStarted(session);

  const {
    phase,
    currentPageIndex,
    values,
    validationErrors,
    currentQuestions,
    canGoBack,
    primaryButtonType,
    specialPage,
    navCta,
    onBack,
    onNext,
    onSubmit,
    onChange,
    onStartSurvey,
    progressPercentage,
    totalQuestionsCount,
    answeredQuestionsCount,
    submitting,
    submitError,
    submitData,
    recordQuestionViewAnalytics,
  } = useSurveyPagination({
    survey,
    session,
    customFieldValues: options?.customFieldValues ?? EMPTY_CUSTOM_FIELD_VALUE_MAP,
  });

  const { recordView } = useRecordQuestionActivity({
    session,
    survey,
    getInstanceCreatedTime,
    onQuestionViewed: recordQuestionViewAnalytics,
  });

  const partialSaveEnabled = isPartialSaveEnabled(options?.savePartialResponse);
  const partialSaveDebounceMs = resolvePartialSaveDebounceMs(options?.savePartialResponse);

  const partialSaveHookResults = usePartialSave({
    session,
    survey,
    answers: values,
    enabled: partialSaveEnabled,
    debounceMs: partialSaveDebounceMs,
    hasUserInteractedRef,
    isFinalSubmitting: submitting,
  });

  const partialSaveResults = useMemo(
    () => (partialSaveEnabled ? partialSaveHookResults : EMPTY_PARTIAL_SAVE_RESULTS),
    [partialSaveEnabled, partialSaveHookResults]
  );

  const engageOnce = useCallback(() => {
    if (phase.kind !== 'content' || hasUserInteractedRef.current) return;
    hasUserInteractedRef.current = true;
    hasSurveySessionStartedRef.current = true;
    markOnce();
  }, [markOnce, phase.kind]);

  const onAction = useCallback(
    (action: SurveyAction) => {
      if (
        action.type === ACTIONS.NEXT &&
        survey &&
        primaryButtonType === 'submit'
      ) {
        action = { type: ACTIONS.SUBMIT };
      }

      sdkLogger.info(`Action dispatched: ${action.type}`, action);
      try {
        switch (action.type) {
          case ACTIONS.START:
            hasSurveySessionStartedRef.current = true;
            onStartSurvey();
            break;
          case ACTIONS.NEXT:
            onNext();
            break;
          case ACTIONS.PREVIOUS:
            onBack();
            break;
          case ACTIONS.SUBMIT:
            void onSubmit();
            break;
          case ACTIONS.SCROLL:
            engageOnce();
            break;
          case ACTIONS.CHANGE:
            engageOnce();
            onChange(action.payload.questionId, action.payload.answerValue);
            break;
          case ACTIONS.RECORD_QUESTION_VIEW:
            if (!hasSurveySessionStartedRef.current) return;
            recordView(action.payload.questionIds);
            break;
          case ACTIONS.RANK_ORDER_SET_RANK: {
            engageOnce();
            const { questionId, optionId, rank } = action.payload;
            const current = normalizeRankOrderAnswers(values[questionId]) as RankOrderAnswers;
            const next = assignRankWithoutDuplicates(current, optionId, rank);
            onChange(questionId, next);
            break;
          }
          case ACTIONS.RANK_ORDER_REORDER: {
            engageOnce();
            const { questionId, orderedOptionIds } = action.payload;
            const next = buildRankOrderFromOrderedOptionIds(orderedOptionIds);
            onChange(questionId, next);
            break;
          }
          case ACTIONS.MCQ_OTHER_TEXT_CHANGE: {
            engageOnce();
            const { questionId, optionId, text } = action.payload;
            onChange(buildMcqOptionLevelKey(questionId, optionId), text);
            break;
          }
          default:
            break;
        }
      } catch (err) {
        sdkLogger.error(`Error executing action: ${action.type}`, { err: toSdkLoggableError(err) });
      }
    },
    [
      onNext,
      onBack,
      onSubmit,
      onChange,
      onStartSurvey,
      survey,
      primaryButtonType,
      engageOnce,
      recordView,
      phase.kind,
      values,
    ]
  );

  return {
    surveyQueryResults: {
      data: survey,
      error: fetchError ?? mapError,
      isLoading: fetching,
    },

    submitSurveyResults: {
      data: submitData,
      error: submitError,
      isLoading: submitting,
    },

    partialSaveResults,

    state: {
      phase,
      currentPageIndex,
      currentQuestions,
      answers: values,
      validationErrors,
      progressPercentage,
      totalQuestionsCount,
      answeredQuestionsCount,
    canGoBack,
    primaryButtonType,
    specialPage,
    navCta,
  },

    onAction,
  };
}
